import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSnackbar } from 'notistack'
import { useRequestHandler } from '@/hooks/useRequestHandler'

import ConfirmEmailStep from '@/pages/getting-started/components/ConfirmEmailStep'

import { useAppSelector } from '@/redux/rootReducer'
import {
  selectAuthUser,
  updateMyProfile,
  selectAuthUserUpdateRequestState,
  checkTwilioVerify,
  startTwilioVerify,
  selectStartTwilioVerifyRequestTracker,
  selectCheckTwilioVerifyRequestTracker
} from '@/redux/reducers/auth.reducer'

export function ConfirmEmailStepContainer(props: { onComplete?: () => void }) {
  const { onComplete } = props
  const { t } = useTranslation()
  const { enqueueSnackbar } = useSnackbar()

  const [email, setEmail] = useState<string>('')
  const [status, setStatus] = useState<'initial' | 'pending'>('initial')

  const updateAuthUserRequestHandler = useRequestHandler({
    onError: () => enqueueSnackbar(t('notification.authUser.update.error'), { variant: 'error' }),
    onSuccess: () => {
      onComplete?.()
    },
    requestTrackerSelector: selectAuthUserUpdateRequestState
  })

  const startEmailVerificationRequestHandler = useRequestHandler({
    onSuccess: () => {
      setStatus('pending')
    },
    onError: () => {
      enqueueSnackbar(t('notification.authUser.startEmailVerification.error'), { variant: 'error' })
    },
    requestTrackerSelector: selectStartTwilioVerifyRequestTracker
  })

  const checkEmailVerificationRequestHandler = useRequestHandler({
    onSuccess: () => {
      updateAuthUserRequestHandler.dispatch(updateMyProfile({ isEmailVerified: true, email }))
    },
    onError: () => {
      enqueueSnackbar(t('notification.authUser.checkEmailVerification.error'), { variant: 'error' })
    },
    requestTrackerSelector: selectCheckTwilioVerifyRequestTracker
  })

  const authUser = useAppSelector(selectAuthUser)

  const initialValues = useMemo(() => ({
    email: authUser?.email || ''
  }), [authUser])

  const handleSendEmail = (email: string) => {
    setEmail(email)
    startEmailVerificationRequestHandler.dispatch(startTwilioVerify({ to: email, channel: 'email' }))
  }

  const handleVerifyEmail = (code: string) => {
    checkEmailVerificationRequestHandler.dispatch(checkTwilioVerify({ to: email, code }))
  }

  return (
    <ConfirmEmailStep
      initialValues={initialValues}
      isSubmitting={startEmailVerificationRequestHandler.requestTracker.loading || checkEmailVerificationRequestHandler.requestTracker.loading}
      onSendEmail={handleSendEmail}
      onVerifyEmail={handleVerifyEmail}
      status={status}
    />
  )
}

export default ConfirmEmailStepContainer